'use client'
import { type FC, useCallback } from 'react'
import type { CartItem, DeliveryDTO, ProductLightDTO } from 'ecosystem'
import { OverridesContextProvider, type OverridesContextType } from 'ui/lib/overrides'
import type { GrandSectionHeadingLink } from 'ui/storefront/layout/GrandSection'
import { FeaturedProducts, type IFeaturedProducts } from '../components'

export type RelatedProductsWidgetBaseConfig = {
  itemCount?: number
  relatedProducts: ProductLightDTO[]
  title: string
  onProductBuy: (item: CartItem) => void
  cartItems: CartItem[]
  onProductRequest: (item: ProductLightDTO) => void
  deliveryOptions: DeliveryDTO[] | null
  overrides?: OverridesContextType
  paths: {
    productsUrlPath: string
  }
  isLoading?: boolean
  headingLink?: GrandSectionHeadingLink
} & Pick<IFeaturedProducts, 'customComponents'>

interface RelatedProductsWidgetBaseProps {
  config: RelatedProductsWidgetBaseConfig
}

const RelatedProductsWidgetBase: FC<RelatedProductsWidgetBaseProps> = ({
  config: {
    overrides,
    itemCount = 20,
    onProductBuy,
    paths,
    relatedProducts,
    title,
    cartItems,
    ...restConfig
  }
}) => {
  const handleAdd = useCallback(
    (product: ProductLightDTO) => {
      const itemInCart = cartItems.find((item) => item.id === product.id)

      const qty = (itemInCart?.qty ?? 0) + 1

      onProductBuy({
        id: product.id,
        type: 'SINGLE',
        qty,
        meta: {
          singleType: {
            product
          }
        }
      })
    },
    [cartItems, onProductBuy]
  )

  const limitedRelatedProducts = relatedProducts.slice(0, itemCount)

  return (
    <OverridesContextProvider overrides={{ ...overrides }}>
      <FeaturedProducts
        items={limitedRelatedProducts}
        onBuy={handleAdd}
        productsUrlPath={paths.productsUrlPath}
        sectionId="products-related"
        title={title || ''}
        {...restConfig}
      />
    </OverridesContextProvider>
  )
}

export default RelatedProductsWidgetBase
